import { PricingOptions, SensitiveContentStatic, YesNoChoice } from "@gamedev-web/one-gaming-crm";

import { CrossVersionPlatformNames } from "../../constants";
import {
  DropdownOption,
  use3dSpatialSoundList,
  useAustraliaAcbRatingsList,
  useBrazilRatingsList,
  useEsrbRatingsList,
  useEuropeIsfeRatingsList,
  useGenresList,
  useGermanyUskRatingsList,
  useJapanCeroRatingsList,
  useKoreaGrbRatingsList,
  useMiddlewareEnginesList,
  useNewZealandOflcRatingsList,
  useRatingsList,
  useRussiaPcbpRatingsList,
  useSaudiArabiaGamrRatingsList,
  useTaiwanCsrrRatingsList,
} from "../../helpers/optionSets";
import { useStrings } from "../../localization/localizationHelpers";
import { AaaShippingType, CrossVersionPlatforms } from "../../models/aaaGameConceptModels";
import { CrossPlay } from "../../models/crossPlay";
import { XboxNetworkAddOns } from "../../models/xboxNetworkAddOns";
import { XboxNetworkFeatures } from "../../models/xboxNetworkFeatures";
import { useLanguagesList } from "../../subForms/idXboxGameInformation/PricingAndAvailabilitySubForm/languagesList";

type CodeLabelMap = Record<string, string> | Map<string, string>;

type PublishingTerritoryValue = Readonly<{ publisherName?: string; subPublisherName?: string }>;

type ReleaseDateValue = Readonly<{ isSelected?: boolean; releaseDate?: string }>;

const toLabelMap = (options: ReadonlyArray<DropdownOption>): Map<string, string> => {
  const map = new Map<string, string>();
  options.forEach(option => {
    if (option.value !== undefined && option.value !== 0 && option.value !== "0") {
      map.set(String(option.value), option.label);
    }
  });
  return map;
};

const hasValue = (code: unknown): boolean =>
  code !== undefined && code !== null && code !== "" && code !== 0 && code !== "0";

const lookupInMap = (map: CodeLabelMap, code: string): string | undefined =>
  map instanceof Map ? map.get(code) : map[code];

const lookupLabel = (map: CodeLabelMap, code: unknown): unknown =>
  hasValue(code) ? lookupInMap(map, String(code)) ?? code : code;

/** Maps an array of option codes to their localized labels, dropping empty entries. */
const mapCodeArray = (value: unknown, map: CodeLabelMap): unknown => {
  if (!Array.isArray(value)) {
    return value;
  }
  return value
    .filter((item): item is string => typeof item === "string" && item.trim() !== "")
    .map(item => lookupInMap(map, item) ?? item);
};

/**
 * Builds a formatter for the read-only game-concept view that converts CRM option
 * codes (genre, middleware, languages, ratings, cross-play, network features, etc.)
 * and the nested territory/date objects into human-readable, localized labels.
 * Without this, the read-only view renders raw codes (e.g. "100000002"),
 * enum keys (e.g. "crossOwnership") or "[object Object]".
 */
export const useReadOnlyValueFormatter = () => {
  const genresMap = toLabelMap(useGenresList());
  const middlewareMap = toLabelMap(useMiddlewareEnginesList());
  const ratingsMap = toLabelMap(useRatingsList());
  const spatial3dSoundMap = toLabelMap(use3dSpatialSoundList());
  const languagesMap = toLabelMap(useLanguagesList());

  const esrbMap = toLabelMap(useEsrbRatingsList());
  const europeIsfeMap = toLabelMap(useEuropeIsfeRatingsList());
  const japanCeroMap = toLabelMap(useJapanCeroRatingsList());
  const brazilMap = toLabelMap(useBrazilRatingsList());
  const germanyUskMap = toLabelMap(useGermanyUskRatingsList());
  const australiaAcbMap = toLabelMap(useAustraliaAcbRatingsList());
  const koreaGrbMap = toLabelMap(useKoreaGrbRatingsList());
  const newZealandOflcMap = toLabelMap(useNewZealandOflcRatingsList());
  const taiwanCsrrMap = toLabelMap(useTaiwanCsrrRatingsList());
  const russiaPcbpMap = toLabelMap(useRussiaPcbpRatingsList());
  const saudiArabiaGamrMap = toLabelMap(useSaudiArabiaGamrRatingsList());

  const [
    esrbTitle,
    europeIsfeTitle,
    japanCeroTitle,
    brazilTitle,
    germanyUskTitle,
    australiaAcbTitle,
    koreaGrbTitle,
    newZealandOflcTitle,
    taiwanCsrrTitle,
    russiaPcbpTitle,
    saudiArabiaGamrTitle,
    yes,
    no,
    maybe,
    other,
  ] = useStrings(
    "aaaEsrbRatingDropdownTitle",
    "aaaEuropeIsfeRatingDropdownTitle",
    "aaaJapanCeroRatingDropdownTitle",
    "aaaBrazilRatingDropdownTitle",
    "aaaGermanyUskRatingDropdownTitle",
    "aaaAustraliaAcbRatingDropdownTitle",
    "aaaKoreaGracRatingDropdownTitle",
    "aaaNewZealandOflcRatingDropdownTitle",
    "aaaTaiwanCsrrRatingDropdownTitle",
    "aaaRussiaPcbpRatingDropdownTitle",
    "aaaSaudiArabiaGamrRatingDropdownTitle",
    "yes",
    "no",
    "maybe",
    "other"
  );

  const [
    windowsStoreVersions,
    win32Versions,
    playStationVersions,
    nintendoSwitchVersions,
    otherVersions,
    crossOwnership,
    crossSave,
    crossProgression,
    sharedAchievements,
    aaaSmartDeliveryLabel,
    dlcPacks,
    durables,
    consumables,
    freeAddOnContent,
    aaaAddOnInGameSubscriptions,
    microsoftStoreOnWindowsPlatform,
    retailAndDigital,
    digitalOnlyAndF2P,
    digitalOnly,
    premium,
    freeToPlay,
  ] = useStrings(
    "windowsStoreVersions",
    "win32Versions",
    "playStationVersions",
    "nintendoSwitchVersions",
    "otherVersions",
    "crossOwnership",
    "crossSave",
    "crossProgression",
    "sharedAchievements",
    "aaaSmartDeliveryLabel",
    "dlcPacks",
    "durables",
    "consumables",
    "freeAddOnContent",
    "aaaAddOnInGameSubscriptions",
    "microsoftStoreOnWindowsPlatform",
    "retailAndDigital",
    "digitalOnlyAndF2P",
    "digitalOnly",
    "premium",
    "freeToPlay"
  );

  const [
    primaryPublisher,
    subPublisher,
    asianTerritory,
    australianTerritory,
    europeanTerritory,
    japanTerritory,
    northAmericanTerritory,
    southAmericanTerritory,
    releaseDateNorthAmerica,
    releaseDateEurope,
    releaseDateJapan,
    releaseDateAsia,
    releaseDateAnz,
    releaseDateSouthAmerica,
    betaLabel,
    demoLabel,
    trialLabel,
  ] = useStrings(
    "primaryPublisher",
    "subPublisher",
    "asianTerritory",
    "australianTerritory",
    "europeanTerritory",
    "japanTerritory",
    "northAmericanTerritory",
    "southAmericanTerritory",
    "aaaReleaseDateNorthAmericaLabel",
    "aaaReleaseDateEuropeLabel",
    "aaaReleaseDateJapanLabel",
    "aaaReleaseDateAsiaLabel",
    "aaaReleaseDateAnzLabel",
    "aaaReleaseDateSouthAmericaLabel",
    "aaaBetaLabel",
    "aaaDemoLabel",
    "aaaTrialLabel"
  );

  const sensitiveContentMap: Record<string, string> = {
    [SensitiveContentStatic.Yes]: yes,
    [SensitiveContentStatic.No]: no,
    [SensitiveContentStatic.Maybe]: maybe,
    [SensitiveContentStatic.Other]: other,
  };

  const yesNoChoiceMap: Record<string, string> = {
    [YesNoChoice.yes]: yes,
    [YesNoChoice.no]: no,
  };

  const shippingStrategyMap: Record<string, string> = {
    [AaaShippingType.retailAndDigital]: retailAndDigital,
    [AaaShippingType.digitalOnlyF2P]: digitalOnlyAndF2P,
    [AaaShippingType.digitalOnly]: digitalOnly,
  };

  const monetizationStrategyMap: Record<string, string> = {
    [PricingOptions.Premium]: premium,
    [PricingOptions.FreeToPlay]: freeToPlay,
  };

  const crossPlayMap: Record<string, string> = {
    [CrossPlay.windowsStoreVersions]: windowsStoreVersions,
    [CrossPlay.win32Versions]: win32Versions,
    [CrossPlay.playStationVersions]: playStationVersions,
    [CrossPlay.nintendoSwitchVersions]: nintendoSwitchVersions,
    [CrossPlay.otherVersions]: otherVersions,
  };

  const networkFeaturesMap: Record<string, string> = {
    [XboxNetworkFeatures.crossOwnership]: crossOwnership,
    [XboxNetworkFeatures.crossSave]: crossSave,
    [XboxNetworkFeatures.crossProgression]: crossProgression,
    [XboxNetworkFeatures.sharedAchievements]: sharedAchievements,
    [XboxNetworkFeatures.smartDelivery]: aaaSmartDeliveryLabel,
  };

  const addOnsMap: Record<string, string> = {
    [XboxNetworkAddOns.dlcPacks]: dlcPacks,
    [XboxNetworkAddOns.durables]: durables,
    [XboxNetworkAddOns.consumables]: consumables,
    [XboxNetworkAddOns.freeAddOnContent]: freeAddOnContent,
    [XboxNetworkAddOns.inGameSubscriptions]: aaaAddOnInGameSubscriptions,
  };

  const crossVersionMap: Record<string, string> = {
    [CrossVersionPlatforms.WindowsStore]: microsoftStoreOnWindowsPlatform,
    [CrossVersionPlatforms.Xbox]: CrossVersionPlatformNames.Xbox,
    [CrossVersionPlatforms.PS4]: CrossVersionPlatformNames.PS4,
    [CrossVersionPlatforms.PS5]: CrossVersionPlatformNames.PS5,
    [CrossVersionPlatforms.Switch]: CrossVersionPlatformNames.Switch,
    [CrossVersionPlatforms.Epic]: CrossVersionPlatformNames.Epic,
    [CrossVersionPlatforms.Steam]: CrossVersionPlatformNames.Steam,
    [CrossVersionPlatforms.Luna]: CrossVersionPlatformNames.Luna,
    [CrossVersionPlatforms.Android]: CrossVersionPlatformNames.Android,
    [CrossVersionPlatforms.iOs]: CrossVersionPlatformNames.iOs,
    [CrossVersionPlatforms.Other]: other,
  };

  const ratingBoards: ReadonlyArray<{ key: string; title: string; map: Map<string, string> }> = [
    { key: "esrb", title: esrbTitle, map: esrbMap },
    { key: "europeIsfe", title: europeIsfeTitle, map: europeIsfeMap },
    { key: "japanCero", title: japanCeroTitle, map: japanCeroMap },
    { key: "brazilRatings", title: brazilTitle, map: brazilMap },
    { key: "germanyUsk", title: germanyUskTitle, map: germanyUskMap },
    { key: "australiaAcb", title: australiaAcbTitle, map: australiaAcbMap },
    { key: "koreaGrb", title: koreaGrbTitle, map: koreaGrbMap },
    { key: "newZealandOflc", title: newZealandOflcTitle, map: newZealandOflcMap },
    { key: "taiwanCsrr", title: taiwanCsrrTitle, map: taiwanCsrrMap },
    { key: "russiaPcbp", title: russiaPcbpTitle, map: russiaPcbpMap },
    { key: "saudiArabiaGamr", title: saudiArabiaGamrTitle, map: saudiArabiaGamrMap },
  ];

  const publishingTerritoryLabels: ReadonlyArray<[string, string]> = [
    ["asia", asianTerritory],
    ["australia", australianTerritory],
    ["europe", europeanTerritory],
    ["japan", japanTerritory],
    ["northAmerica", northAmericanTerritory],
    ["southAmerica", southAmericanTerritory],
  ];

  const releaseDateTerritoryLabels: ReadonlyArray<[string, string]> = [
    ["northAmerica", releaseDateNorthAmerica],
    ["europe", releaseDateEurope],
    ["japan", releaseDateJapan],
    ["asia", releaseDateAsia],
    ["anz", releaseDateAnz],
    ["southAmerica", releaseDateSouthAmerica],
  ];

  const demosTrialsBetasLabels: ReadonlyArray<[string, string]> = [
    ["betas", betaLabel],
    ["demos", demoLabel],
    ["trials", trialLabel],
  ];

  const formatPublishingTerritories = (value: unknown): unknown => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }
    const obj = value as Record<string, PublishingTerritoryValue | undefined>;
    return publishingTerritoryLabels
      .map(([key, label]) => {
        const territory = obj[key];
        if (!territory) {
          return null;
        }
        const parts: string[] = [];
        if (territory.publisherName?.trim()) {
          parts.push(`${primaryPublisher}: ${territory.publisherName}`);
        }
        if (territory.subPublisherName?.trim()) {
          parts.push(`${subPublisher}: ${territory.subPublisherName}`);
        }
        return parts.length > 0 ? `${label} — ${parts.join(", ")}` : null;
      })
      .filter((entry): entry is string => Boolean(entry));
  };

  const formatReleaseDates = (value: unknown, labels: ReadonlyArray<[string, string]>): unknown => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }
    const obj = value as Record<string, ReleaseDateValue | undefined>;
    return labels
      .map(([key, label]) => {
        const entry = obj[key];
        if (!entry || (!entry.isSelected && !entry.releaseDate?.trim())) {
          return null;
        }
        return entry.releaseDate?.trim() ? `${label}: ${entry.releaseDate}` : label;
      })
      .filter((entry): entry is string => Boolean(entry));
  };

  return (data: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
    if (!data) {
      return data;
    }
    const result: Record<string, unknown> = { ...data };

    // Scalar CRM option codes.
    if ("genre" in result) {
      result.genre = lookupLabel(genresMap, result.genre);
    }
    if ("middleware" in result) {
      result.middleware = lookupLabel(middlewareMap, result.middleware);
    }
    if ("spatial3dSound" in result) {
      result.spatial3dSound = lookupLabel(spatial3dSoundMap, result.spatial3dSound);
    }

    // Scalar enum / static-option codes.
    if ("shippingStrategy" in result && hasValue(result.shippingStrategy)) {
      result.shippingStrategy =
        shippingStrategyMap[String(result.shippingStrategy)] ?? result.shippingStrategy;
    }
    if ("monetizationStrategy" in result && hasValue(result.monetizationStrategy)) {
      result.monetizationStrategy =
        monetizationStrategyMap[String(result.monetizationStrategy)] ?? result.monetizationStrategy;
    }
    if ("sensitiveContent" in result && hasValue(result.sensitiveContent)) {
      result.sensitiveContent =
        sensitiveContentMap[String(result.sensitiveContent)] ?? result.sensitiveContent;
    }
    for (const key of [
      "xboxPlayAnywhere",
      "isResponsibleForCertification",
      "isPrevIndependentlyPublished",
      "isAssumingResponsibilitesForPrevPublishedGame",
    ]) {
      if (key in result && hasValue(result[key])) {
        result[key] = yesNoChoiceMap[String(result[key])] ?? result[key];
      }
    }

    // Arrays of option codes.
    if ("supportedLanguages" in result) {
      result.supportedLanguages = mapCodeArray(result.supportedLanguages, languagesMap);
    }
    if ("crossPlay" in result) {
      result.crossPlay = mapCodeArray(result.crossPlay, crossPlayMap);
    }
    if ("xboxNetworkFeatures" in result) {
      result.xboxNetworkFeatures = mapCodeArray(result.xboxNetworkFeatures, networkFeaturesMap);
    }
    if ("addOns" in result) {
      result.addOns = mapCodeArray(result.addOns, addOnsMap);
    }
    for (const key of [
      "xboxLiveSignIn",
      "xboxAchievements",
      "xboxCrossPlay",
      "xboxCrossSaveProgress",
      "windowsCrossSaveProgress",
    ]) {
      if (key in result) {
        result[key] = mapCodeArray(result[key], crossVersionMap);
      }
    }

    // Anticipated ratings: AAA object of per-board codes, ID single scalar code.
    if ("anticipatedRatings" in result) {
      const ratings = result.anticipatedRatings;
      if (ratings && typeof ratings === "object" && !Array.isArray(ratings)) {
        const ratingsObj = ratings as Record<string, unknown>;
        result.anticipatedRatings = ratingBoards
          .map(board => {
            const code = ratingsObj[board.key];
            if (!hasValue(code)) {
              return null;
            }
            const label = board.map.get(String(code)) ?? String(code);
            return `${board.title}: ${label}`;
          })
          .filter((entry): entry is string => Boolean(entry));
      } else if (hasValue(ratings)) {
        result.anticipatedRatings = ratingsMap.get(String(ratings)) ?? String(ratings);
      }
    }

    // Nested territory / date objects.
    if ("publishingTerritories" in result) {
      result.publishingTerritories = formatPublishingTerritories(result.publishingTerritories);
    }
    if ("releaseDatesByTerritory" in result) {
      result.releaseDatesByTerritory = formatReleaseDates(
        result.releaseDatesByTerritory,
        releaseDateTerritoryLabels
      );
    }
    if ("demosTrialsBetas" in result) {
      result.demosTrialsBetas = formatReleaseDates(result.demosTrialsBetas, demosTrialsBetasLabels);
    }

    return result;
  };
};
